import clsx from 'clsx';
import styles from './dataAvailability.module.scss';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { infoIconGreen, infoIconYellow, infoToolTip } from '../../../../../assets/hostedassets';
import { GstConnectionStatus, GstListItem } from '../../../../../store/dashboard/revenueVendorExpenses/types';
import { isPrimaryGstStatus } from '../../../../../store/investor/constants';
import { DATA_AVAILABILITY_TOOLTIP_LABEL, DATA_AVAILABLE_ROW_LABEL, GST_DATA_SOURCES } from './constants';

export interface MonthlyGst {
    gst: string;
    state?: string | null;
    dataSource?: string | null;
}

type GstMonthErrors = GstConnectionStatus['gstMonthErrors'];

interface DataAvailabilityTooltipProps {
    gsts?: MonthlyGst[];
    month?: string;
    gstList: GstListItem[];
    gstMonthErrors?: GstMonthErrors;
}

// GSTR-1 / GSTR-2A availability comes from period totals, which carry no dataSource: a GSTIN listed
// for the month with a null source still has data.
const hasNoDataSource = (monthlyGst?: MonthlyGst) =>
    !!monthlyGst && monthlyGst.dataSource !== GST_DATA_SOURCES.CONNECTED && monthlyGst.dataSource !== GST_DATA_SOURCES.UPLOADED;
const isConnectedOrWithoutSource = (monthlyGst?: MonthlyGst) =>
    monthlyGst?.dataSource === GST_DATA_SOURCES.CONNECTED || hasNoDataSource(monthlyGst);

export function renderDataAvailabilityTooltip({ gsts = [], month, gstList, gstMonthErrors }: DataAvailabilityTooltipProps) {
    return (
        <div className={styles.Tooltip}>
            <div className={styles.TopText}>Charts/table are generated only for GST's with Data Available:</div>
            <div className={styles.Gsts}>
                {gstList?.map((gst, i) => {
                    const monthlyGst = gsts?.find((monthlyGst) => monthlyGst.gst === gst.gstNumber);
                    const monthError = month ? gstMonthErrors?.[gst.gstNumber]?.[month] : undefined;
                    return (
                        <div className={styles.GstRow} key={i}>
                            <div className={styles.Left}>
                                <span className={styles.State}>{gst?.state} </span>
                                <span>{gst?.gstNumber}</span>
                            </div>
                            <div className={styles.Right}>
                                <div
                                    className={clsx(styles.Badge, {
                                        [styles.NotConnected]: !monthlyGst && !gst?.connected,
                                        [styles.NoData]: !monthlyGst && gst?.connected,
                                        [styles.Connected]: isConnectedOrWithoutSource(monthlyGst),
                                        [styles.Uploaded]: monthlyGst?.dataSource === GST_DATA_SOURCES.UPLOADED,
                                    })}
                                >
                                    {monthlyGst?.dataSource === GST_DATA_SOURCES.CONNECTED || hasNoDataSource(monthlyGst) ? (
                                        DATA_AVAILABLE_ROW_LABEL
                                    ) : monthlyGst?.dataSource === GST_DATA_SOURCES.UPLOADED ? (
                                        'Data Uploaded'
                                    ) : gst?.connected ? (
                                        <>
                                            No Data
                                            {monthError?.message && (
                                                <Tooltip content={monthError.message} direction={'top'} fitContent small usePortal>
                                                    <button type="button" className={styles.ErrorIconButton}>
                                                        <img src={infoToolTip} alt="info" height={'13px'} className={styles.ErrorIcon} />
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </>
                                    ) : (
                                        'Not Connected'
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/** GSTINs shown and counted for one month: every primary-status GSTIN, plus any other GSTIN that has data that month. */
export const filterGstListForMonth = (gstList: GstListItem[], gsts: MonthlyGst[] = []): GstListItem[] =>
    gstList.filter((gst) => isPrimaryGstStatus(gst.gstStatus) || gsts.some((monthlyGst) => monthlyGst.gst === gst.gstNumber));

type DataAvailabilityBadgeProps = DataAvailabilityTooltipProps;

/** The "x/y" Data Available badge (+ per-GSTIN tooltip) for one month of one return. */
export function getDataAvailabilityBadge({ gsts, month, gstList, gstMonthErrors }: DataAvailabilityBadgeProps) {
    const monthlyGstCount = gsts?.length ?? '0';
    const visibleGstList = filterGstListForMonth(gstList ?? [], gsts);
    const totalGstCount = visibleGstList.length;
    // A month with no GSTINs to expect (0/0) has no data, so it stays yellow whether gsts is missing or empty.
    const isComplete = totalGstCount > 0 && Number(monthlyGstCount) === totalGstCount;
    return (
        <div
            className={clsx(styles.DataCountBadge, {
                [styles.Green]: isComplete,
                [styles.Yellow]: !isComplete,
            })}
        >
            {monthlyGstCount}/{totalGstCount}
            <Tooltip
                content={renderDataAvailabilityTooltip({ gsts, month, gstList: visibleGstList, gstMonthErrors })}
                direction={'top'}
                usePortal
                focusable
                ariaLabel={DATA_AVAILABILITY_TOOLTIP_LABEL}
            >
                <img className={styles.InfoIcon} src={isComplete ? infoIconGreen : infoIconYellow} alt={''} height={'14px'} />
            </Tooltip>
        </div>
    );
}
